import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Music2, Sparkles, Cloud, Shield, BarChart3, Rocket, Headphones } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1632582204758-5ac65783517a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4Njg2NzUyNXww&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1541126274323-dbac58d14741?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkaiUyMGxpdmUlMjBwZXJmb3JtYW5jZXxlbnwwfHx8fDE3ODY4Njc1MjV8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div className="relative">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="studio" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/70 to-zinc-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 text-cyan-400 text-xs font-semibold tracking-widest uppercase">
              <Sparkles className="w-3.5 h-3.5" /> Invite-Only Distribution
            </span>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05]">
              Release your sound to the <span className="gradient-text">whole world</span>.
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
              SonotekDigi is a curated music distribution platform. Accounts are provisioned by our team —
              artists focus on the music, we handle the pipeline.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/login" data-testid="hero-login-btn">
                <Button size="lg" className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110 px-7">
                  <Rocket className="w-4 h-4 mr-2" /> Artist Login
                </Button>
              </Link>
              <Link to="/about" data-testid="hero-about-btn">
                <Button size="lg" variant="outline" className="rounded-full border-zinc-700 hover:bg-zinc-900 px-7">
                  Learn More
                </Button>
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-8 text-sm text-zinc-500">
              <div className="flex items-center gap-2"><div className="flex items-end gap-0.5 h-4"><span className="wave-bar" style={{width:2}}/><span className="wave-bar" style={{width:2}}/><span className="wave-bar" style={{width:2}}/></div> 24/7 Delivery</div>
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /> Secure Uploads</div>
              <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-400" /> Real-time Status</div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 mb-4">Built For Artists</div>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                A studio-grade pipeline for your <span className="gradient-text">next release</span>.
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Every submission is tracked, reviewed, and archived with DDEX-grade metadata.
                ISRC and UPC codes are auto-assigned from our reserved pool the moment your submission lands.
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden border border-zinc-800">
                <img src={FEATURE_IMG} alt="performance" className="w-full aspect-[4/3] object-cover" />
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Cloud, title: "Master Storage", desc: "Upload WAV or MP3 masters up to studio quality." },
                { icon: Shield, title: "Private Dashboards", desc: "Every artist gets an isolated dashboard. Your catalog is never visible to others." },
                { icon: BarChart3, title: "Live Status", desc: "Follow every submission from Under Review to Released." },
                { icon: Headphones, title: "Copyright Support", desc: "File YouTube takedown requests directly from your dashboard." },
              ].map((f, i) => (
                <div key={i} className="group p-6 rounded-2xl bg-zinc-950/60 border border-zinc-800 hover:border-cyan-500/50 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-violet-500/10 blur-3xl" />
            <div className="relative">
              <h3 className="text-3xl sm:text-4xl font-bold tracking-tight">Already an artist with us?</h3>
              <p className="mt-4 text-zinc-400 max-w-lg">Sign in to submit your next release.</p>
              <Link to="/login" data-testid="cta-login-btn">
                <Button size="lg" className="mt-8 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110 px-8">
                  <Music2 className="w-4 h-4 mr-2" /> Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Sonotek Digital" className="h-7 w-auto rounded bg-white/95 p-0.5 border border-zinc-800" />
            <div>© {new Date().getFullYear()} Sonotek<span className="gradient-text font-semibold">Digi</span>. All rights reserved.</div>
          </div>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-zinc-300">About</Link>
            <Link to="/login" className="hover:text-zinc-300">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
